import { supabase } from '../../lib/supabase';
import type {
  AuthorizedPool,
  DashboardOverview,
  ActivityDayPoint,
  TopPlayer,
  TopPlayerMetric,
  RecentActivityEvent,
  ActivitySearchOption,
  EngagementComparison,
  SigningPlayerComparison,
  RosterInjuryNews,
  PlayerOwnership,
  IncompletePooler,
} from './types';

/**
 * Wrappers RPC pour le Community Intelligence Dashboard.
 *
 * Contrat de retour : { data, error } — jamais lancer, la couche UI gère
 * l'affichage des erreurs. `PGRST116` (no rows) est considéré non-fatal.
 *
 * Toutes ces RPC exigent (côté serveur) que le couple (p_user_id, p_league_id)
 * ait `leagues_users.dashboardadmin = true`. Sinon elles répondent avec
 * ERRCODE 42501 ("Not authorized for this pool"). On propage cette erreur
 * telle quelle : la UI la convertit en écran d'accès refusé.
 */

type Result<T> = { data: T | null; error: unknown };

export async function fetchAuthorizedPools(userId: number): Promise<Result<AuthorizedPool[]>> {
  const { data, error } = await supabase.rpc('ci_is_dashboard_admin', { p_user_id: userId });
  if (error && (error as { code?: string }).code !== 'PGRST116') {
    return { data: null, error };
  }
  const list = Array.isArray(data)
    ? data.map((row: Record<string, unknown>) => ({
        league_id: Number(row.league_id),
        league_name: String(row.league_name ?? ''),
        league_type: String(row.league_type ?? ''),
        joined_at: (row.joined_at as string | null) ?? null,
      }))
    : [];
  return { data: list, error: null };
}

export async function fetchDashboardOverview(
  userId: number,
  leagueId: number
): Promise<Result<DashboardOverview>> {
  const { data, error } = await supabase.rpc('ci_get_dashboard_overview', {
    p_user_id: userId,
    p_league_id: leagueId,
  });
  if (error) return { data: null, error };
  return { data: (data as DashboardOverview) ?? null, error: null };
}

export async function fetchActivityTimeseries(
  userId: number,
  leagueId: number,
  days: number
): Promise<Result<ActivityDayPoint[]>> {
  const { data, error } = await supabase.rpc('ci_get_activity_timeseries', {
    p_user_id: userId,
    p_league_id: leagueId,
    p_days: days,
  });
  if (error) return { data: null, error };
  const list = Array.isArray(data)
    ? data.map((row: Record<string, unknown>) => ({
        day: String(row.day),
        trades: Number(row.trades ?? 0),
        signings: Number(row.signings ?? 0),
        drops: Number(row.drops ?? 0),
        ltir_placed: Number(row.ltir_placed ?? 0),
        ltir_reactivated: Number(row.ltir_reactivated ?? 0),
        new_joiners: Number(row.new_joiners ?? 0),
      }))
    : [];
  return { data: list, error: null };
}

export async function fetchTopPlayers(
  userId: number,
  leagueId: number,
  metric: TopPlayerMetric,
  days: number | null,
  limit: number
): Promise<Result<TopPlayer[]>> {
  const { data, error } = await supabase.rpc('ci_get_top_players', {
    p_user_id: userId,
    p_league_id: leagueId,
    p_metric: metric,
    p_days: days,
    p_limit: limit,
  });
  if (error) return { data: null, error };
  const list = Array.isArray(data)
    ? data.map((row: Record<string, unknown>) => ({
        player_id: Number(row.player_id),
        player_name: (row.player_name as string | null) ?? null,
        event_count: Number(row.event_count ?? 0),
      }))
    : [];
  return { data: list, error: null };
}

export async function searchActivityOptions(
  userId: number,
  leagueId: number,
  entity: 'pooler' | 'player',
  query: string,
  limit = 10
): Promise<Result<ActivitySearchOption[]>> {
  const { data, error } = await supabase.rpc('ci_search_activity_options', {
    p_user_id: userId,
    p_league_id: leagueId,
    p_entity: entity,
    p_query: query,
    p_limit: limit,
  });
  if (error) return { data: null, error };
  const rows = Array.isArray(data)
    ? data.map((row: Record<string, unknown>) => ({
        id: Number(row.entity_id),
        label: String(row.label ?? ''),
      }))
    : [];
  return { data: rows, error: null };
}

export async function fetchFilteredActivity(
  userId: number,
  leagueId: number,
  filters: {
    eventType: 'all' | RecentActivityEvent['event_type'];
    poolerId: number | null;
    playerId: number | null;
    dateFrom: string | null;
    dateTo: string | null;
    limit: number;
    offset: number;
  }
): Promise<Result<RecentActivityEvent[]>> {
  const { data, error } = await supabase.rpc('ci_get_filtered_activity', {
    p_user_id: userId,
    p_league_id: leagueId,
    p_event_type: filters.eventType,
    p_pooler_id: filters.poolerId,
    p_player_id: filters.playerId,
    p_date_from: filters.dateFrom,
    p_date_to: filters.dateTo,
    p_limit: filters.limit,
    p_offset: filters.offset,
  });
  if (error) return { data: null, error };
  const rows = Array.isArray(data)
    ? data.map((row: Record<string, unknown>) => ({
        event_type: row.event_type as RecentActivityEvent['event_type'],
        event_time: String(row.event_time),
        user_id: row.user_id != null ? Number(row.user_id) : null,
        username: (row.username as string | null) ?? null,
        player_id: row.player_id != null ? Number(row.player_id) : null,
        player_name: (row.player_name as string | null) ?? null,
        details: (row.details as Record<string, unknown>) ?? {},
      }))
    : [];
  return { data: rows, error: null };
}

export async function fetchEngagementComparison(
  userId: number,
  leagueId: number,
  days: number
): Promise<Result<EngagementComparison>> {
  const { data, error } = await supabase.rpc('ci_get_engagement_comparison', {
    p_user_id: userId,
    p_league_id: leagueId,
    p_days: days,
  });
  if (error) return { data: null, error };
  return { data: (data as EngagementComparison) ?? null, error: null };
}

export async function fetchSigningPlayerComparison(
  userId: number,
  leagueId: number,
  days: number
): Promise<Result<SigningPlayerComparison[]>> {
  const { data, error } = await supabase.rpc('ci_get_signing_player_comparison', {
    p_user_id: userId,
    p_league_id: leagueId,
    p_days: days,
  });
  if (error) return { data: null, error };
  const rows = Array.isArray(data)
    ? data.map((row: Record<string, unknown>) => ({
        player_id: Number(row.player_id),
        player_name: (row.player_name as string | null) ?? null,
        event_count: Number(row.current_count ?? 0),
        current_count: Number(row.current_count ?? 0),
        previous_count: Number(row.previous_count ?? 0),
        latest_event_at: (row.latest_event_at as string | null) ?? null,
      }))
    : [];
  return { data: rows, error: null };
}

export async function fetchRosterInjuryNews(
  userId: number,
  leagueId: number,
  days = 7
): Promise<Result<RosterInjuryNews[]>> {
  const { data, error } = await supabase.rpc('ci_get_roster_injury_news', {
    p_user_id: userId,
    p_league_id: leagueId,
    p_days: days,
  });
  if (error) return { data: null, error };
  const rows = Array.isArray(data)
    ? data.map((row: Record<string, unknown>) => ({
        event_type: row.event_type as RosterInjuryNews['event_type'],
        player_id: Number(row.player_id),
        player_name: (row.player_name as string | null) ?? null,
        event_time: String(row.event_time),
        status: (row.status as string | null) ?? null,
        description: (row.description as string | null) ?? null,
      }))
    : [];
  return { data: rows, error: null };
}

/**
 * Détention actuelle des joueurs demandés dans les rosters du pool. Complément
 * facultatif : la page Opportunités reste utilisable si la RPC est absente.
 */
export async function fetchPlayerOwnership(
  userId: number,
  leagueId: number,
  playerIds: number[]
): Promise<Result<PlayerOwnership[]>> {
  const ids = [...new Set(playerIds.filter((id) => Number.isFinite(id)))].slice(0, 200);
  if (ids.length === 0) return { data: [], error: null };
  const { data, error } = await supabase.rpc('ci_get_player_ownership', {
    p_user_id: userId,
    p_league_id: leagueId,
    p_player_ids: ids,
  });
  if (error) return { data: null, error };
  const rows = Array.isArray(data)
    ? data.map((row: Record<string, unknown>) => ({
        player_id: Number(row.player_id),
        owners: Number(row.owners ?? 0),
        total_poolers: Number(row.total_poolers ?? 0),
        active_owners: Number(row.active_owners ?? 0),
        ltir_owners: Number(row.ltir_owners ?? 0),
      }))
    : [];
  return { data: rows, error: null };
}

/**
 * Poolers du pool dont l'équipe est incomplète, avec leur adresse courriel.
 */
export async function fetchIncompletePoolers(
  userId: number,
  leagueId: number
): Promise<Result<IncompletePooler[]>> {
  const { data, error } = await supabase.rpc('ci_get_incomplete_poolers', {
    p_user_id: userId,
    p_league_id: leagueId,
  });
  if (error) return { data: null, error };
  const rows = Array.isArray(data)
    ? data.map((row: Record<string, unknown>) => ({
        user_id: Number(row.user_id),
        username: (row.username as string | null) ?? null,
        email: (row.email as string | null) ?? null,
        joined_at: (row.joined_at as string | null) ?? null,
      }))
    : [];
  return { data: rows, error: null };
}

/**
 * Détecte si une erreur RPC correspond à un refus d'autorisation côté serveur
 * (colonne `dashboardadmin` false pour ce couple). Utilisé par la UI pour
 * afficher un écran "Accès refusé" plutôt qu'une erreur générique.
 */
export function isAuthorizationError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as { code?: string; message?: string };
  if (e.code === '42501') return true;
  if (typeof e.message === 'string' && /not authorized/i.test(e.message)) return true;
  return false;
}
